/**
 * PerformanceOverlay - Mobile-readable performance stats for debugging
 * 
 * Shows:
 * - FPS / Frame time
 * - Draw calls / Triangles / Geometries / Textures
 * - DPR (Device Pixel Ratio)
 * - Active lights / Shadow lights
 * - Remote players
 * - WebSocket messages/sec
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useThree, useFrame } from '@react-three/fiber';

// Performance stats collector (runs inside Canvas)
export function PerformanceStatsCollector({ onStats }) {
  const { gl, scene } = useThree();
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());
  const frameTimes = useRef([]);
  const lastStatsUpdate = useRef(0);
  
  useFrame(() => {
    const now = performance.now();
    const delta = now - lastTime.current;
    lastTime.current = now;
    
    // Track frame times for averaging
    frameTimes.current.push(delta);
    if (frameTimes.current.length > 60) {
      frameTimes.current.shift();
    }
    
    frameCount.current++;
    
    // Update stats every 500ms
    if (now - lastStatsUpdate.current > 500) {
      lastStatsUpdate.current = now;
      
      const avgFrameTime = frameTimes.current.reduce((a, b) => a + b, 0) / frameTimes.current.length;
      const fps = Math.round(1000 / avgFrameTime);
      
      // Get renderer info
      const info = gl.info;
      
      // Count lights and shadow-casting lights
      let totalLights = 0;
      let shadowLights = 0;
      scene.traverse((obj) => {
        if (obj.isLight) {
          totalLights++;
          if (obj.castShadow) shadowLights++;
        }
      });
      
      if (onStats) {
        onStats({
          fps,
          frameTime: avgFrameTime.toFixed(1),
          drawCalls: info.render?.calls || 0,
          triangles: info.render?.triangles || 0,
          geometries: info.memory?.geometries || 0,
          textures: info.memory?.textures || 0,
          dpr: gl.getPixelRatio(),
          totalLights,
          shadowLights,
          programs: info.programs?.length || 0,
        });
      }
    }
  });
  
  return null;
}

// UI Overlay component (renders outside Canvas)
export function PerformanceOverlayUI({ 
  stats, 
  remotePlayers = 0, 
  wsMessagesPerSec = 0,
  visible = false 
}) {
  if (!visible || !stats) return null;
  
  return (
    <div 
      className="fixed top-14 left-2 z-50 bg-black/80 text-green-400 font-mono text-xs p-2 rounded pointer-events-none"
      style={{ minWidth: '160px' }}
      data-testid="performance-overlay"
    >
      <div className="space-y-0.5">
        <div className="flex justify-between">
          <span>FPS:</span>
          <span className={stats.fps < 30 ? 'text-red-400' : stats.fps < 50 ? 'text-yellow-400' : 'text-green-400'}>
            {stats.fps}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Frame:</span>
          <span className={parseFloat(stats.frameTime) > 33 ? 'text-red-400' : 'text-green-400'}>
            {stats.frameTime}ms
          </span>
        </div>
        <div className="border-t border-green-900 my-1" />
        <div className="flex justify-between">
          <span>Draws:</span>
          <span>{stats.drawCalls}</span>
        </div>
        <div className="flex justify-between">
          <span>Tris:</span>
          <span>{(stats.triangles / 1000).toFixed(1)}k</span>
        </div>
        <div className="flex justify-between">
          <span>Geom:</span>
          <span>{stats.geometries}</span>
        </div>
        <div className="flex justify-between">
          <span>Tex:</span>
          <span>{stats.textures}</span>
        </div>
        <div className="border-t border-green-900 my-1" />
        <div className="flex justify-between">
          <span>DPR:</span>
          <span className={stats.dpr > 2 ? 'text-yellow-400' : 'text-green-400'}>
            {stats.dpr.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Lights:</span>
          <span>{stats.totalLights}</span>
        </div>
        <div className="flex justify-between">
          <span>Shadow:</span>
          <span className={stats.shadowLights > 3 ? 'text-yellow-400' : 'text-green-400'}>
            {stats.shadowLights}
          </span>
        </div>
        <div className="border-t border-green-900 my-1" />
        <div className="flex justify-between">
          <span>Players:</span>
          <span>{remotePlayers}</span>
        </div>
        <div className="flex justify-between">
          <span>WS/s:</span>
          <span>{wsMessagesPerSec}</span>
        </div>
      </div>
    </div>
  );
}

// Hook to track WebSocket message rate
export function useWsMessageRate() {
  const messageCount = useRef(0);
  const [rate, setRate] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setRate(messageCount.current);
      messageCount.current = 0;
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  const incrementCount = useCallback(() => {
    messageCount.current++;
  }, []);
  
  return { rate, incrementCount };
}

export default PerformanceOverlayUI;
